import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { addToCart, setCurrentScreen, clearCart } from '../../store/orderSlice';
import { CartItem, OptionChoice } from '../../types';
import Button from '../ui/Button';
import StartOverButton from '../ui/StartOverButton';
import CartImage from '../../data/PanaImages/CartImage.png';
import CheckMark from '../../data/PanaImages/CheckMark.png';

const nexaRustStack = '"Nexa Rust Sans Black A", "Museo Sans", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif';
const museoStack = '"Museo Sans", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif';

const ItemDetailScreen: React.FC = () => {
  const dispatch = useDispatch();
  const { selectedMenuItem } = useSelector((state: RootState) => state.order);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, OptionChoice[]>>({});
  const [quantity, setQuantity] = useState(1);
  const [showAddedOverlay, setShowAddedOverlay] = useState(false);
  const overlayTimeoutRef = useRef<number | null>(null);

  const navigateToMenu = useCallback(() => {
    if (overlayTimeoutRef.current !== null) {
      window.clearTimeout(overlayTimeoutRef.current);
      overlayTimeoutRef.current = null;
    }
    setShowAddedOverlay(false);
    dispatch(setCurrentScreen('menu'));
  }, [dispatch]);

  // Lock page scroll while this screen is active so only the options panel can scroll
  useEffect(() => {
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.body.style.overflow = originalBodyOverflow;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (overlayTimeoutRef.current !== null) {
        window.clearTimeout(overlayTimeoutRef.current);
        overlayTimeoutRef.current = null;
      }
    };
  }, []);

  if (!selectedMenuItem) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="p-12 text-center">
          <h2 className="text-kiosk-3xl font-bold text-kiosk-text mb-6">No item selected</h2>
          <Button onClick={() => dispatch(setCurrentScreen('menu'))}>
            Back to Menu
          </Button>
        </div>
      </div>
    );
  }

  const handleOptionSelect = (
    optionId: string,
    choice: OptionChoice,
    isMulti: boolean,
    maxSelect: number
  ) => {
    setSelectedOptions((prev) => {
      const current = prev[optionId] || [];
      if (!isMulti) {
        return { ...prev, [optionId]: [choice] };
      }
      const exists = current.some((c) => c.id === choice.id);
      if (exists) {
        return { ...prev, [optionId]: current.filter((c) => c.id !== choice.id) };
      }
      if (current.length >= maxSelect) {
        return prev; // do not exceed max selections
      }
      return { ...prev, [optionId]: [...current, choice] };
    });
  };

  const calculatePrice = () => {
    const basePrice = selectedMenuItem.price;
    const optionsPrice = Object.values(selectedOptions)
      .flat()
      .reduce((sum, opt) => sum + opt.price, 0);
    return (basePrice + optionsPrice) * quantity;
  };

  const handleAddToCart = () => {
    const cartItem: CartItem = {
      id: `${selectedMenuItem.id}-${Date.now()}`,
      menuItem: selectedMenuItem,
      selectedOptions: Object.values(selectedOptions).flat(),
      quantity,
      totalPrice: calculatePrice()
    };

    dispatch(addToCart(cartItem));
    setShowAddedOverlay(true);

    if (overlayTimeoutRef.current !== null) {
      window.clearTimeout(overlayTimeoutRef.current);
    }

    overlayTimeoutRef.current = window.setTimeout(() => {
      navigateToMenu();
    }, 4000);
  };

  const canAddToCart = () => {
    if (!selectedMenuItem.options) return true;
    return selectedMenuItem.options.every((option) => {
      const selected = selectedOptions[option.id] || [];
      const min = option.minSelect ?? (option.required ? 1 : 0);
      const max = option.maxSelect ?? 1;
      if (selected.length < min) return false;
      if (selected.length > max) return false;
      return true;
    });
  };

  const handleBack = () => {
    dispatch(setCurrentScreen('menu'));
  };

  const handleStartOver = () => {
    dispatch(clearCart());
    dispatch(setCurrentScreen('welcome'));
  };

  // Price split for larger display styles
  const [randPart, centsPart] = selectedMenuItem.price.toFixed(2).split('.');

  const FALLBACK_IMG = 'https://images.spurcorp.com/D16EAE6E-A30A-4E5B-A97A-F654B1647789';
  const hasOptions = Boolean(selectedMenuItem.options && selectedMenuItem.options.length > 0);

  return (
    <div className="h-screen flex flex-col bg-[#f2f1ec] overflow-hidden">
      <div className="flex-shrink-0">
        <div className="w-full aspect-[21/9] bg-gray-100">
          <img
            src={selectedMenuItem.image && selectedMenuItem.image.length > 0 ? selectedMenuItem.image : FALLBACK_IMG}
            alt={selectedMenuItem.displayName}
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).src = FALLBACK_IMG;
            }}
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col pl-10 pr-0 pt-8">
        <h1 className="font-display font-black text-kiosk-text tracking-[1.5px] text-[64px] leading-[64px] uppercase text-left">
          {selectedMenuItem.displayName}
        </h1>

        <div className="mt-8 flex-1 min-h-0 flex flex-row gap-8">
          <div className={`${hasOptions ? 'basis-[35%]' : 'flex-1'} flex flex-col gap-6`}>
            <p className="text-[20px] text-kiosk-text leading-relaxed max-w-[640px] text-left">
              {selectedMenuItem.description}
            </p>
            <div className="text-primary flex items-start">
              <span className="font-display font-black text-[68px] leading-none">{randPart}</span>
              <span
                className="ml-1 text-[28px] leading-none self-start mt-1"
                style={{
                  fontFamily:
                    '"Nexa Rust Sans Black A", "Nexa Rust Sans", "Museo Sans", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
                }}
              >
                {centsPart}
              </span>
            </div>
          </div>

          {hasOptions && (
            <div className="basis-[65%] min-h-0 flex">
              <div className="flex-1 min-h-0 bg-white rounded-l-2xl rounded-r-none shadow-[0_8px_24px_rgba(0,0,0,0.12)]">
                <div
                  className="h-full min-h-0 overflow-y-auto pr-6 pl-6 py-6 pb-[24rem] overscroll-contain will-change-scroll touch-pan-y"
                  onWheel={(e) => e.stopPropagation()}
                >
                  <h2 className="font-display font-black text-kiosk-text tracking-[1.5px] text-[64px] leading-[64px] uppercase text-left mb-6">
                    MAKE IT <span className="text-[#E32B26]">YOURS</span>
                  </h2>
                  {selectedMenuItem.options && selectedMenuItem.options.length > 0 && (
                    <div className="space-y-6">
                      {selectedMenuItem.options.map((option) => (
                        <div key={option.id} className="">
                          <div className="mb-3 text-left">
                            {option.required && (
                              <span className="inline-block mb-2 px-3 py-1 text-[12px] font-bold uppercase tracking-wide text-white bg-red-600 rounded-md shadow-sm">
                                Required
                              </span>
                            )}
                            <h3 className="text-[24px] font-semibold text-kiosk-text text-left">
                              {option.displayName}
                            </h3>
                          </div>
                          <div className="space-y-3">
                            {option.choices.map((choice) => {
                              const selectedArray = selectedOptions[option.id] || [];
                              const isMulti = (option.maxSelect ?? 1) > 1;
                              const isSelected = selectedArray.some((c) => c.id === choice.id);
                              return (
                                <label key={choice.id} className="block">
                                    {isMulti ? (
                                    <input
                                      type="checkbox"
                                      name={`${option.id}-${choice.id}`}
                                      checked={isSelected}
                                      onChange={(e) => { e.stopPropagation(); handleOptionSelect(option.id, choice, true, option.maxSelect ?? 99); }}
                                      className="absolute opacity-0 pointer-events-none"
                                    />
                                  ) : (
                                    <input
                                      type="radio"
                                      name={option.id}
                                      value={choice.id}
                                      checked={isSelected}
                                      onChange={(e) => { e.stopPropagation(); handleOptionSelect(option.id, choice, false, 1); }}
                                      className="absolute opacity-0 pointer-events-none"
                                    />
                                  )}
                                  <div
                                    className={`flex items-center justify-between px-5 py-4 rounded-xl cursor-pointer select-none transition-all min-h-touch-md shadow-sm text-left border-2 ${
                                      isSelected ? 'border-primary shadow-md' : 'border-gray-300'
                                    }`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleOptionSelect(option.id, choice, isMulti, isMulti ? (option.maxSelect ?? 99) : 1);
                                    }}
                                  >
                                    <div className="flex items-center">
                                      <span className="relative mr-4">
                                        {isMulti ? (
                                          <span
                                            className={`relative grid place-items-center w-6 h-6 rounded-md border-2 ${
                                              isSelected ? 'border-primary bg-white' : 'border-gray-300 bg-white'
                                            }`}
                                          >
                                            <span
                                              className={`block w-4 h-4 rounded-sm ${
                                                isSelected ? 'bg-red-500' : 'bg-transparent'
                                              }`}
                                            />
                                            {isSelected && (
                                              <svg
                                                className="absolute w-4 h-4 text-white"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                              >
                                                <path d="M20 6L9 17L4 12" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                              </svg>
                                            )}
                                          </span>
                                        ) : (
                                          <>
                                            <span
                                              className={`block w-6 h-6 rounded-full border-2 ${
                                                isSelected ? 'border-primary' : 'border-gray-300'
                                              }`}
                                            />
                                            <span
                                              className={`absolute inset-0 m-auto w-3 h-3 rounded-full ${
                                                isSelected ? 'bg-red-500' : 'bg-transparent'
                                              }`}
                                            />
                                          </>
                                        )}
                                      </span>
                                      <span className="text-[18px] text-kiosk-text">{choice.displayName}</span>
                                    </div>
                                    {choice.price > 0 ? (
                                      (() => {
                                        const [rands, cents] = choice.price.toFixed(2).split('.');
                                        const colorClass = isSelected ? 'text-primary' : 'text-kiosk-text';
                                        const randsSize = isSelected ? 'text-[26px]' : 'text-[22px]';
                                        const centsSize = isSelected ? 'text-[14px]' : 'text-[12px]';
                                        return (
                                          <span className={`flex items-start font-display font-black ${colorClass}`}>
                                            <span className={`${randsSize} leading-none`}>{rands}</span>
                                            <span className={`ml-1 ${centsSize} leading-none self-start mt-1`}>{cents}</span>
                                          </span>
                                        );
                                      })()
                                    ) : (
                                      (() => {
                                        const colorClass = isSelected ? 'text-primary' : 'text-kiosk-text';
                                        const randsSize = isSelected ? 'text-[26px]' : 'text-[22px]';
                                        return (
                                          <span className={`flex items-start font-display font-black ${colorClass}`}>
                                            <span className={`${randsSize} leading-none`}>Free</span>
                                          </span>
                                        );
                                      })()
                                    )}
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom fixed footer */}
      <div className="fixed left-0 right-0 bottom-0 z-50">
        <div className="bg-white rounded-t-2xl shadow-[0_-10px_30px_rgba(0,0,0,0.15)] px-10 py-6">
          {/* Row 1: Quantity + Total */}
          <div className="relative flex items-center justify-center">
            {/* Quantity control - pill box */}
            <div className="inline-flex items-center bg-white border-2 border-gray-300 rounded-xl px-4 py-2 shadow-sm">
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                disabled={quantity <= 1}
                className={`px-3 text-[40px] font-extrabold ${quantity <= 1 ? 'text-gray-300' : 'text-gray-600'}`}
                style={{
                  fontFamily:
                    '"Nexa Rust Sans Black A", "Nexa Rust Sans", "Museo Sans", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
                }}
              >
                –
              </button>
              <span
                className="mx-4 font-display font-black text-[40px] leading-none text-kiosk-text select-none"
                style={{
                  fontFamily:
                    '"Nexa Rust Sans Black A", "Nexa Rust Sans", "Museo Sans", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
                }}
              >
                {quantity}
              </span>
              <button
                onClick={() => setQuantity(quantity + 1)}
                className="px-3 text-[40px] font-extrabold text-primary"
                style={{
                  fontFamily:
                    '"Nexa Rust Sans Black A", "Nexa Rust Sans", "Museo Sans", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
                }}
              >
                +
              </button>
            </div>

            {/* Total */}
          <div className="absolute right-0 top-1/2 -translate-y-1/2 flex items-start text-kiosk-text">
              <span className="font-display font-black text-[44px] leading-none">R {calculatePrice().toFixed(2).split('.')[0]}</span>
              <span
                className="ml-1 text-[24px] leading-none self-start mt-1"
                style={{
                  fontFamily:
                    '"Nexa Rust Sans Black A", "Nexa Rust Sans", "Museo Sans", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
                }}
              >
                {calculatePrice().toFixed(2).split('.')[1]}
              </span>
            </div>
          </div>

          {/* Row 2: Actions */}
          <div className="mt-5 flex items-center gap-6">
            <Button
              variant="outline"
              size="xl"
              onClick={handleBack}
              className="w-2/5 px-10 uppercase tracking-[1.2px] text-[40px] whitespace-nowrap !border-primary !text-primary"
              style={{
                fontFamily:
                  '"Nexa Rust Sans Black A", "Nexa Rust Sans", "Museo Sans", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
              }}
            >
              Cancel Item
            </Button>
            <Button
              variant="primary"
              size="xl"
              onClick={handleAddToCart}
              disabled={!canAddToCart()}
              className="flex-1 px-14 uppercase tracking-[1.2px] text-[40px] font-display rounded-tl-none rounded-tr-[18px] rounded-bl-[18px] rounded-br-none"
              style={{
                fontFamily:
                  '"Nexa Rust Sans Black A", "Nexa Rust Sans", "Museo Sans", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
              }}
            >
              Add to Order
            </Button>
          </div>
        </div>
        <div className="w-full flex justify-center bg-white pt-2 pb-0">
          <StartOverButton onClick={handleStartOver} />
        </div>
      </div>

      {showAddedOverlay && (
        <div
          className="fixed inset-0 z-[250] flex items-center justify-center px-8"
          style={{ backgroundColor: '#f2f1ec' }}
          onClick={navigateToMenu}
          role="presentation"
        >
          <div className="flex flex-col items-center text-center gap-6">
            <div className="relative w-[100px] h-[100px]">
              <img src={CartImage} alt="Item added" className="w-full h-full object-contain" />
              <img
                src={CheckMark}
                alt="Added"
                className="absolute -top-4 -right-4 w-[40px] h-[40px]"
              />
            </div>
            <h2
              className="font-display font-black uppercase tracking-[2px] text-[#185C53]"
              style={{ fontFamily: nexaRustStack, fontSize: '80px', lineHeight: '84px' }}
            >
              Item Added
            </h2>
            <p
              className="text-[#333333]"
              style={{ fontFamily: museoStack, fontSize: '28px', lineHeight: '36px' }}
            >
              Your total has been updated.
            </p>
            <div className="flex items-start text-[#2F3437]">
              <span
                className="font-display font-black text-[64px] leading-none"
                style={{ fontFamily: nexaRustStack }}
              >
                R {calculatePrice().toFixed(2).split('.')[0]}
              </span>
              <span
                className="ml-1 text-[32px] leading-none self-start mt-1"
                style={{ fontFamily: nexaRustStack }}
              >
                {calculatePrice().toFixed(2).split('.')[1]}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemDetailScreen; 